import { AuthenticationResult, EventType } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { useEffect } from "react";
import { ReactNode, useState } from "react";
import { useAuthStore } from "../store/useAuthStore";
import { b2cInstance } from "./b2cInstance";
import { msalInstance } from "./msalInstance";

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const authInstance = useAuthStore.getState().isB2C
    ? b2cInstance
    : msalInstance;
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    authInstance.enableAccountStorageEvents();

    // Restore account from cache on mount
    const currentAccount = authInstance.getActiveAccount();
    if (!currentAccount) {
      const allAccounts = authInstance.getAllAccounts();
      if (allAccounts.length > 0) {
        authInstance.setActiveAccount(allAccounts[0]);
      }
    }

    // Handle login success
    const callbackId = authInstance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const authResult = event.payload as AuthenticationResult;
        if (authResult?.account) {
          authInstance.setActiveAccount(authResult.account);
        }
      }
    });

    // Mark init complete
    setInitialized(true);

    // Cleanup
    return () => {
      if (callbackId) {
        authInstance.removeEventCallback(callbackId);
      }
    };
  }, [authInstance]);

  // Delay render until account check completes
  if (!initialized) return null;

  return <MsalProvider instance={authInstance}>{children}</MsalProvider>;
};
